import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Center, Group, Loader, Modal, Paper, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import useApi from '../hooks/useApi';
import {
  addProspectsToCampaign,
  approveV2Campaign,
  deleteCampaign,
  discoverV2Campaign,
  fetchCampaign,
  fetchCampaigns,
  findV2CampaignProspects,
  fetchMyPrincipal,
  fetchV2Flows,
  patchCampaign,
  patchTouch,
} from '../services/api';
import useSetupWizard from '../v2/useSetupWizard';
import WizardFrame from '../v2/WizardFrame';
import LauncherScreen from '../v2/screens/LauncherScreen';
import WelcomeScreen from '../v2/screens/WelcomeScreen';
import CombinedDesignScreen from '../v2/screens/CombinedDesignScreen';
import OpenListScreen from '../v2/screens/OpenListScreen';
import LocationScreen from '../v2/screens/LocationScreen';
import FreetextScreen from '../v2/screens/FreetextScreen';
import NarrowFiltersScreen from '../v2/screens/NarrowFiltersScreen';
import HelpScreen from '../v2/screens/HelpScreen';
import DiscoveringScreen from '../v2/screens/DiscoveringScreen';
import ProspectsScreen from '../v2/screens/ProspectsScreen';
import MessagesScreen from '../v2/screens/MessagesScreen';
import FindingScreen from '../v2/screens/FindingScreen';
import LaunchedScreen from '../v2/screens/LaunchedScreen';
import TodayScreen from '../v2/screens/TodayScreen';
import LaunchConfirmModal from '../v2/LaunchConfirmModal';
import { PHASES, COACH_PHASES } from '../v2/phases';
import { isOrganicCampaign } from '../utils/organic';

function questionStepIndex(step) {
  const match = /^q(\d+)$/.exec(step);
  return match ? Number(match[1]) : -1;
}

function resolvePhase(step, { isCoachFlow = false, phases = PHASES } = {}) {
  const [design, wordsmith, prospects, launch] = PHASES.map((phase) => phase.key);
  if (step === 'launcher') return { currentKey: null, completedKeys: [] };
  if (step === 'today') return { currentKey: null, completedKeys: [] };
  if (isCoachFlow) {
    const coachDesign = phases[0]?.key || 'design';
    const coachLaunch = phases[1]?.key || 'launch';
    if (step === 'launched') {
      return { currentKey: coachLaunch, completedKeys: [coachDesign] };
    }
    return { currentKey: coachDesign, completedKeys: [] };
  }
  if (step === 'discovering' || step === 'messages') {
    return { currentKey: wordsmith, completedKeys: [design] };
  }
  if (step === 'finding' || step === 'prospects') {
    return { currentKey: prospects, completedKeys: [design, wordsmith] };
  }
  if (step === 'launched') {
    return { currentKey: launch, completedKeys: [design, wordsmith, prospects] };
  }
  const isQuestionStep = /^q\d+$/.test(step);
  if (step === 'design' || isQuestionStep) return { currentKey: design, completedKeys: [] };
  return { currentKey: null, completedKeys: [] };
}

function QuestionScreen({ question, value, onChange }) {
  if (!question) return null;
  if (question.type === 'openlist') {
    return <OpenListScreen question={question} value={value} onChange={onChange} />;
  }
  if (question.type === 'location') {
    return <LocationScreen question={question} value={value} onChange={onChange} />;
  }
  if (question.type === 'freetext') {
    return <FreetextScreen question={question} value={value} onChange={onChange} />;
  }
  if (question.type === 'narrowfilters') {
    return <NarrowFiltersScreen question={question} value={value} onChange={onChange} />;
  }
  if (question.type === 'pickone_editable') {
    return <HelpScreen question={question} value={value} onChange={onChange} />;
  }
  return <Text c="red" size="sm">Unknown question type: {question.type}</Text>;
}

export default function AviaryV2Page() {
  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const [flows, setFlows] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [approving, setApproving] = useState(false);
  // Distinct from `approving` — stays true after the API succeeds so the AFTER panel
  // remains visible on the launched screen. Cleared when the wizard resets.
  const [launched, setLaunched] = useState(false);
  const [cancellingSetup, setCancellingSetup] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  // 'closed' | 'loading' (Apollo query in-flight) | 'ready' (count populated) | 'empty' (zero reachable)
  const [launchModalState, setLaunchModalState] = useState('closed');
  const [pendingCoachLaunch, setPendingCoachLaunch] = useState(null);
  const [importedCount, setImportedCount] = useState(0);
  const [retryKey, setRetryKey] = useState(0);
  const [customOpenerSaving, setCustomOpenerSaving] = useState(false);
  const [principal, setPrincipal] = useState(null);
  const [campaignCount, setCampaignCount] = useState(null); // null while loading
  const [loadingEdit, setLoadingEdit] = useState(false);
  const skipHomeResetRef = useRef(true);
  const loadedEditCampaignIdRef = useRef(null);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const editMode = searchParams.get('mode') === 'edit';
  const editCampaignId = searchParams.get('campaignId');

  const {
    flow,
    step,
    answers,
    proposal,
    manualProspects,
    selectedProspectIds,
    prospectDrafts,
    error,
    setFlow,
    setAnswer,
    initFromCampaign,
    setProposal,
    addManualProspect,
    toggleProspect,
    setSelectedProspectIds,
    setProspectDraft,
    setError,
    next,
    back,
    reset,
    canAdvance,
    setStep,
    goToDiscovering,
    goToProspects,
    goToMessages,
    goToFinding,
    goToLaunched,
  } = useSetupWizard();
  const flowId = String(flow?.id || principal?.flowId || '').toLowerCase();
  const isCoachFlow = flowId === 'exec_coach';
  const phases = isCoachFlow ? COACH_PHASES : PHASES;

  // Phase bar click — jump back to any completed phase's landing step. Preserves state; skips loader steps.
  const handlePhaseClick = useCallback((phaseKey) => {
    const target = phases.find((p) => p.key === phaseKey);
    if (target?.landingStep) setStep(target.landingStep);
  }, [phases, setStep]);

  useEffect(() => {
    let cancelled = false;
    fetchV2Flows(api)
      .then((data) => { if (!cancelled) setFlows(data); })
      .catch((err) => { if (!cancelled) setFetchError(err.response?.data?.error || err.message); });
    fetchMyPrincipal(api)
      .then((data) => { if (!cancelled) setPrincipal(data); })
      .catch(() => { if (!cancelled) setPrincipal({}); }); // treat missing principal as unassigned; fall back to launcher
    fetchCampaigns(api)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data?.campaigns || []);
        setCampaignCount(list.filter((campaign) => !isOrganicCampaign(campaign)).length);
      })
      .catch(() => { if (!cancelled) setCampaignCount(0); });
    return () => { cancelled = true; };
  }, [api]);

  // Route the user based on principal + campaign state whenever we land on 'launcher' with data loaded.
  // Fires on first mount AND after every reset() (which snaps back to 'launcher') — so returning users
  // always land on Today, and single-flow principals never see the flow picker.
  // Special case: ?new=1 (sidebar "setup" icon) forces a fresh wizard even if the principal
  // has existing campaigns — we jump straight to 'design' and strip the query so refresh
  // doesn't re-trigger.
  useEffect(() => {
    if (step !== 'launcher') return;
    if (!flows || !principal || campaignCount === null) return;
    if (editMode && editCampaignId) {
      if (loadedEditCampaignIdRef.current === editCampaignId) return;
      loadedEditCampaignIdRef.current = editCampaignId;
      setLoadingEdit(true);
      fetchCampaign(api, editCampaignId)
        .then((detail) => {
          const campaign = detail?.campaign || null;
          if (!campaign) throw new Error('Campaign not found');
          const flowId = campaign?.brief?.flowId || principal?.flowId;
          const flowObj = flows.find((f) => f.id === flowId) || flows[0];
          if (flowObj) setFlow(flowObj);
          initFromCampaign({
            campaign,
            touches: Array.isArray(detail?.touches) ? detail.touches : [],
            strategyNote: detail?.strategyNote || campaign?.strategyNote || '',
          });
          setStep('design');
        })
        .catch((err) => {
          notifications.show({
            title: 'Could not open campaign editor',
            message: err.response?.data?.error || err.message || 'Campaign not found',
            color: 'red',
          });
          navigate('/v2/campaigns', { replace: true });
        })
        .finally(() => setLoadingEdit(false));
      return;
    }
    loadedEditCampaignIdRef.current = null;
    const flowId = principal.flowId;
    const wantsNew = location.search.includes('new=1');
    if (wantsNew && flowId) {
      const flowObj = flows.find((f) => f.id === flowId);
      if (flowObj) {
        setFlow(flowObj);
        setStep('design');
        navigate('/v2', { replace: true });
        return;
      }
    }
    if (flowId && campaignCount > 0) {
      setStep('today');
      return;
    }
    if (flowId) {
      const flowObj = flows.find((f) => f.id === flowId);
      if (flowObj) {
        setFlow(flowObj);
        setStep('design');
      }
    }
    // Else: unassigned principal — stay on Launcher (fallback).
  }, [
    step,
    flows,
    principal,
    campaignCount,
    setStep,
    setFlow,
    location.search,
    navigate,
    editMode,
    editCampaignId,
    api,
    initFromCampaign,
  ]);

  // Clicking Home in the V2 sidebar while mid-wizard re-navigates to /v2 and
  // bumps location.key — bounce back. Reset takes step to 'launcher' and the
  // auto-route effect immediately forwards to 'today' or 'q0' as appropriate.
  useEffect(() => {
    if (skipHomeResetRef.current) {
      skipHomeResetRef.current = false;
      return;
    }
    if (
      location.pathname === '/v2'
      && step !== 'launcher'
      && step !== 'launched'
      && step !== 'today'
    ) {
      reset();
    }
  }, [location.key]); // intentionally only location.key

  const handleProposal = useCallback((p) => {
    setError(null);
    setProposal(p);
    if (isCoachFlow) {
      setStep('design');
      return;
    }
    goToMessages();
  }, [isCoachFlow, setError, setProposal, goToMessages, setStep]);

  const handleTouchUpdate = useCallback((index, updatedTouch) => {
    setProposal((prev) => {
      if (!prev?.touches) return prev;
      const nextTouches = prev.touches.map((t, i) => (i === index ? { ...t, ...updatedTouch } : t));
      return { ...prev, touches: nextTouches };
    });
  }, [setProposal]);

  const handleCustomOpenerChange = useCallback(async (nextEnabled) => {
    if (!proposal?.campaign?.id) return;
    const campaignId = proposal.campaign.id;
    const previousProposal = proposal;
    const openerTouch = (proposal.touches || [])[0];

    setCustomOpenerSaving(true);
    setProposal((prev) => {
      if (!prev) return prev;
      const nextTouches = [...(prev.touches || [])];
      if (!nextEnabled && nextTouches[0]) {
        nextTouches[0] = { ...nextTouches[0], messageTemplate: '', smsTemplate: '' };
      }
      return {
        ...prev,
        campaign: { ...prev.campaign, customOpener: nextEnabled },
        touches: nextTouches,
      };
    });

    try {
      const updatedCampaign = await patchCampaign(api, campaignId, { customOpener: nextEnabled });
      setProposal((prev) => (
        prev
          ? { ...prev, campaign: { ...prev.campaign, ...updatedCampaign } }
          : prev
      ));

      if (!nextEnabled && openerTouch?.id) {
        const updatedTouch = await patchTouch(api, campaignId, openerTouch.id, {
          messageTemplate: '',
          smsTemplate: '',
        });
        handleTouchUpdate(0, updatedTouch);
      }
    } catch (err) {
      setProposal(previousProposal);
      notifications.show({
        title: 'Could not update opener mode',
        message: err.response?.data?.error || err.message || 'Please try again.',
        color: 'red',
      });
    } finally {
      setCustomOpenerSaving(false);
    }
  }, [api, proposal, setProposal, handleTouchUpdate]);

  const handleProspectsFound = useCallback((response) => {
    setError(null);
    setProposal((prev) => (prev ? {
      ...prev,
      prospects: response.prospects || [],
      warning: response.warning || null,
      hiddenUnreachable: Number(response.hiddenUnreachable || 0),
    } : prev));
    // Preselect ALL newly discovered prospects — the coach untick anything they don't
    // want. Cheaper cognitive load than picking 5 out of 17.
    const allNew = (response.prospects || [])
      .filter((x) => !x.existing && x.externalId)
      .map((x) => String(x.externalId));
    setSelectedProspectIds(allNew);
    goToProspects();
  }, [setError, setProposal, setSelectedProspectIds, goToProspects]);

  const handleCoachDesignLaunch = useCallback(async () => {
    if (!isCoachFlow || !flow?.id) return;
    if (approving) return; // guard against double-click while Apollo is running
    setApproving(true);
    setPendingCoachLaunch(null);
    setLaunchModalState('loading'); // open modal immediately so the coach sees feedback
    try {
      // Always call discover in edit mode so the campaign's `targeting` reflects the
      // coach's latest Design answers. For fresh campaigns, discover creates a new
      // one; for edit/duplicate re-launch, it upserts targeting on the existing doc.
      let workingProposal = proposal;
      if (!workingProposal?.campaign?.id || editMode) {
        workingProposal = await discoverV2Campaign(api, {
          flowId: flow.id,
          answers,
          campaignId: editMode ? editCampaignId : null,
        });
        setProposal(workingProposal);
      }

      const campaignId = workingProposal?.campaign?.id;
      if (!campaignId) throw new Error('Campaign setup is not ready yet. Please try again.');

      const found = await findV2CampaignProspects(api, campaignId, editMode ? 'edit' : '');
      const discoveredProspects = Array.isArray(found?.prospects) ? found.prospects : [];
      const selectedProspects = discoveredProspects.filter((item) => !item?.existing);

      setProposal((prev) => ({
        ...(prev || workingProposal || {}),
        prospects: discoveredProspects,
        warning: found?.warning || null,
        hiddenUnreachable: Number(found?.hiddenUnreachable || 0),
      }));
      setSelectedProspectIds(selectedProspects.map((item) => String(item.externalId || '')).filter(Boolean));

      if (selectedProspects.length === 0) {
        // Three distinct zero-selectable cases — different remedies for each:
        //   1. Apollo returned people but all are already in the pipeline → adjust targeting for fresh names.
        //   2. Apollo enrichment 402'd on most candidates → top up credits.
        //   3. Apollo genuinely returned nothing matching → change criteria.
        if (found?.warningCode === 'apollo_credit_exhausted') {
          setLaunchModalState('credit_exhausted');
        } else if (discoveredProspects.length > 0) {
          // Apollo found people but they're all `existing` in this principal's pipeline.
          setPendingCoachLaunch({ existingCount: discoveredProspects.length });
          setLaunchModalState('all_existing');
        } else {
          setLaunchModalState('empty');
        }
      } else {
        setPendingCoachLaunch({
          campaignId,
          selectedProspects,
          count: selectedProspects.length,
        });
        setLaunchModalState('ready');
      }
    } catch (err) {
      setLaunchModalState('closed');
      notifications.show({
        title: 'Failed to prepare launch',
        message: err.response?.data?.error || err.message || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setApproving(false);
    }
  }, [
    isCoachFlow,
    approving,
    flow,
    proposal,
    api,
    answers,
    editMode,
    editCampaignId,
    setProposal,
    setSelectedProspectIds,
  ]);

  const handleCoachLaunchConfirm = useCallback(async () => {
    if (!pendingCoachLaunch?.campaignId) return;
    setApproving(true);
    try {
      const result = await approveV2Campaign(api, pendingCoachLaunch.campaignId, {
        selectedProspects: pendingCoachLaunch.selectedProspects || [],
      });
      setImportedCount(result?.imported || pendingCoachLaunch.count || 0);
      setLaunched(true);
      setLaunchModalState('closed');
      setPendingCoachLaunch(null);
      goToLaunched();
    } catch (err) {
      // Modal stays open in 'ready' state so the coach can retry.
      notifications.show({
        title: 'Failed to launch campaign',
        message: err.response?.data?.error || err.message || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setApproving(false);
    }
  }, [pendingCoachLaunch, api, goToLaunched]);

  const handleAsyncError = useCallback((message) => {
    setError(message);
  }, [setError]);

  function buildSelectedProspects() {
    const discovered = proposal?.prospects || [];
    const all = [...discovered, ...manualProspects];
    // Merge per-prospect edits (opener + manual coaching notes) so the /approve payload
    // reflects what the coach saw and tweaked on the Prospects screen.
    return all
      .filter((p) => selectedProspectIds.has(String(p.externalId)) && !p.existing)
      .map((p) => {
        const draft = prospectDrafts[String(p.externalId)] || {};
        return {
          ...p,
          personalizedOpener: draft.personalizedOpener !== undefined ? draft.personalizedOpener : p.personalizedOpener,
          manualCoachNotes: draft.manualCoachNotes !== undefined ? draft.manualCoachNotes : (p.manualCoachNotes || null),
        };
      });
  }

  async function handleApprove() {
    if (!proposal?.campaign?.id) return;
    setApproving(true);
    try {
      const selectedProspects = buildSelectedProspects();
      const isEditRun = editMode && editCampaignId === proposal.campaign.id;

      if (isEditRun) {
        let result;
        const campaignStatus = String(proposal.campaign.status || '').toLowerCase();
        if (campaignStatus === 'draft') {
          result = await approveV2Campaign(api, proposal.campaign.id, { selectedProspects });
        } else {
          result = await addProspectsToCampaign(api, proposal.campaign.id, { selectedProspects });
        }
        notifications.show({
          title: 'Campaign updated',
          message: `${result?.imported || 0} new prospects added.`,
          color: 'sage',
        });
        navigate(`/v2/campaigns/${proposal.campaign.id}`, {
          state: { back: { path: '/v2/campaigns', label: 'Campaigns' } },
        });
      } else {
        const result = await approveV2Campaign(api, proposal.campaign.id, {
          selectedProspects,
        });
        setImportedCount(result.imported || 0);
        setLaunched(true);
        goToLaunched();
      }
    } catch (err) {
      notifications.show({
        title: editMode ? 'Failed to update campaign' : 'Failed to launch campaign',
        message: err.response?.data?.error || err.message || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setApproving(false);
    }
  }

  // Full reset — also clears the local launch state so a fresh wizard doesn't
  // resurrect the previous AFTER panel.
  const handleFullReset = useCallback(() => {
    setLaunched(false);
    setImportedCount(0);
    setLaunchModalState('closed');
    setPendingCoachLaunch(null);
    reset();
  }, [reset]);

  async function handleCancelSetupConfirmed() {
    const campaignId = proposal?.campaign?.id;
    setCancellingSetup(true);
    try {
      if (campaignId) {
        await deleteCampaign(api, campaignId);
      }
      notifications.show({
        title: 'Setup cancelled',
        message: 'Draft setup discarded.',
        color: 'gray',
      });
      reset();
    } catch (err) {
      notifications.show({
        title: 'Cleanup incomplete',
        message: "Couldn't fully clean up the draft — check Campaigns in the main app if it lingers.",
        color: 'red',
      });
      reset();
    } finally {
      setCancellingSetup(false);
      setCancelModalOpen(false);
      setLaunchModalState('closed');
      setPendingCoachLaunch(null);
    }
  }

  // Gate the initial render on flows + principal + campaignCount so the auto-route effect can decide
  // between Launcher / Today / straight-to-Q0 before anything shows. Avoids a Launcher flash for
  // returning users who should land on Today.
  const stillLoading = ((!flows || !principal || campaignCount === null) || loadingEdit) && !fetchError;
  if (stillLoading) {
    return (
      <Center h="100vh" style={{ background: 'var(--aviary-canvas)' }}>
        <Loader color="sage" />
      </Center>
    );
  }

  const questions = flow?.questions || [];
  const totalQuestions = questions.length;
  const qIdx = questionStepIndex(step);
  const currentQuestion = qIdx >= 0 ? questions[qIdx] : null;
  const isLastQuestion = qIdx >= 0 && qIdx === totalQuestions - 1;
  const selectedCount = selectedProspectIds.size;
  const { currentKey: currentPhaseKey, completedKeys: completedPhaseKeys } = resolvePhase(step, { isCoachFlow, phases });

  let primaryLabel;
  let primaryDisabled = false;
  let showBack = false;
  let backLabel = 'Back';
  let secondaryLabel;
  let dangerLabel;
  let hideCta = false;
  let onPrimary;
  let onSecondary;
  let onDanger;

  if (step === 'launcher') {
    hideCta = true;
  } else if (step === 'today') {
    // Today screen now has its own inline "Start a new campaign" button in the footer —
    // hide the sticky footer CTA so we don't double up.
    hideCta = true;
  } else if (step === 'design') {
    primaryLabel = isCoachFlow ? 'Launch »' : 'Write my messages »';
    primaryDisabled = !canAdvance;
    showBack = true;
    backLabel = 'Back';
    onPrimary = () => {
      // Trim any freetext answers before advancing.
      (flow?.questions || []).forEach((q) => {
        if (q.type === 'freetext') {
          setAnswer(q.key, String(answers[q.key] || '').trim());
        }
      });
      if (isCoachFlow) {
        handleCoachDesignLaunch();
      } else {
        next();
      }
    };
  } else if (qIdx >= 0) {
    // Legacy per-question step path — kept for backward compat, unused by primary flow.
    if (isLastQuestion) primaryLabel = isCoachFlow ? 'Launch »' : 'Write my messages »';
    else if (qIdx === 0) primaryLabel = "Let's go";
    else primaryLabel = 'Next';
    primaryDisabled = !canAdvance;
    showBack = true;
    backLabel = qIdx === 0 ? 'Back' : '← Previous';
    onPrimary = () => {
      if (currentQuestion?.type === 'freetext') {
        setAnswer(currentQuestion.key, String(answers[currentQuestion.key] || '').trim());
      }
      if (isCoachFlow && isLastQuestion) {
        handleCoachDesignLaunch();
      } else {
        next();
      }
    };
  } else if (!isCoachFlow && (step === 'discovering' || step === 'finding')) {
    hideCta = !error;
    if (error) {
      secondaryLabel = 'Try again';
      onSecondary = () => {
        setError(null);
        setRetryKey((k) => k + 1);
        if (step === 'discovering') goToDiscovering();
        else goToFinding();
      };
    }
  } else if (!isCoachFlow && step === 'messages') {
    primaryLabel = 'Find prospects »';
    // Only gate on the opener (touch 0) — touches 1+ are drafted in the background
    // and no longer surfaced in the Wordsmith UI (v2.0.2 — follow-up section dropped).
    const opener = (proposal?.touches || [])[0];
    const customOpenerEnabled = proposal?.campaign?.customOpener !== false;
    const isLinkedin = String(proposal?.campaign?.channel || '').toLowerCase() === 'linkedin';
    const openerDrafted = Boolean(opener && String(opener.messageTemplate || '').trim());
    const openerLinkUnresolved = Boolean(
      opener
        && /\{\{link\}\}/i.test(String(opener.messageTemplate || ''))
        && !String(opener.destinationUrl || '').trim()
    );
    primaryDisabled = customOpenerEnabled || !isLinkedin
      ? (!openerDrafted || openerLinkUnresolved)
      : false;
    showBack = true;
    backLabel = '← Design';
    onPrimary = () => goToFinding();
    dangerLabel = 'Cancel setup';
    onDanger = () => setCancelModalOpen(true);
  } else if (step === 'prospects' && !isCoachFlow) {
    primaryLabel = editMode
      ? (selectedCount > 0 ? `Review & save ${selectedCount} »` : 'Review & save »')
      : (selectedCount > 0 ? `Review & launch to ${selectedCount} »` : 'Review & launch »');
    primaryDisabled = selectedCount === 0 || approving;
    showBack = true;
    backLabel = '← Wordsmith';
    // Deferred approve — Launch step now owns the actual API call so the coach can
    // review the campaign summary first (Tranche C).
    onPrimary = () => goToLaunched();
  } else if (step === 'launched') {
    // Launched screen has inline Back / Launch / See pipeline buttons — hide the footer CTA entirely.
    hideCta = true;
  }

  const showExit = step !== 'launcher' && step !== 'launched';

  return (
    <>
      <Modal
        opened={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        title="Cancel setup?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This will discard your drafts, messages, and any pages I built. You'll go back to the start.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setCancelModalOpen(false)} disabled={cancellingSetup}>
              Keep going
            </Button>
            <Button color="red" onClick={handleCancelSetupConfirmed} loading={cancellingSetup}>
              Yes, cancel
            </Button>
          </Group>
        </Stack>
      </Modal>

      <WizardFrame
        primaryLabel={hideCta ? undefined : primaryLabel}
        onPrimary={onPrimary}
        primaryDisabled={primaryDisabled}
        showBack={showBack}
        backLabel={backLabel}
        onBack={back}
        secondaryLabel={secondaryLabel}
        onSecondary={onSecondary}
        dangerLabel={dangerLabel}
        onDanger={onDanger}
        hideCta={hideCta && !secondaryLabel}
        showExit={showExit}
        onExit={reset}
        currentPhaseKey={currentPhaseKey}
        completedPhaseKeys={completedPhaseKeys}
        onPhaseClick={handlePhaseClick}
        phases={phases}
        maxWidth={step === 'prospects' || step === 'launched' ? 1080 : 560}
      >
        {step === 'launcher' && (
          <LauncherScreen
            flows={flows}
            fetchError={fetchError}
            onSelect={(selected) => {
              setFlow(selected);
              next();
            }}
          />
        )}

        {step === 'today' && (
          <TodayScreen
            principalName={principal?.name || ''}
            onStartCampaign={() => {
              // Jump directly into the principal's flow (skip Launcher — one flow per principal).
              const flowObj = flows?.find((f) => f.id === principal?.flowId);
              if (flowObj) {
                setFlow(flowObj);
                setStep('design');
              } else {
                reset();
              }
            }}
          />
        )}

        {step === 'design' && (
          <CombinedDesignScreen
            flow={flow}
            answers={answers}
            onAnswerChange={setAnswer}
            api={api}
          />
        )}

        {qIdx >= 0 && currentQuestion && (
          <Stack gap="xl">
            {qIdx === 0 && <WelcomeScreen flow={flow} />}
            <QuestionScreen
              question={currentQuestion}
              value={answers[currentQuestion.key]}
              onChange={(value) => setAnswer(currentQuestion.key, value)}
            />
          </Stack>
        )}

        {!isCoachFlow && step === 'discovering' && !error && (
          <DiscoveringScreen
            key={`discover-${retryKey}`}
            flow={flow}
            answers={answers}
            campaignId={editMode ? editCampaignId : null}
            onProposal={handleProposal}
            onError={handleAsyncError}
          />
        )}

        {!isCoachFlow && step === 'finding' && !error && (
          <FindingScreen
            key={`find-${retryKey}`}
            campaignId={proposal?.campaign?.id}
            mode={editMode ? 'edit' : ''}
            onFound={handleProspectsFound}
            onError={handleAsyncError}
          />
        )}

        {!isCoachFlow && (step === 'discovering' || step === 'finding') && error && (
          <Paper p="xl" radius="md" withBorder style={{ background: 'var(--aviary-surface)' }}>
            <Stack gap="md">
              <Text fw={600} style={{ color: 'var(--aviary-ink)' }}>
                {step === 'discovering' ? "Couldn't build the plan" : "Couldn't find prospects"}
              </Text>
              <Text size="sm" c="red">{error}</Text>
              <Button
                color="sage"
                w="fit-content"
                onClick={() => {
                  setError(null);
                  setRetryKey((k) => k + 1);
                }}
              >
                Try again
              </Button>
            </Stack>
          </Paper>
        )}

        {step === 'prospects' && !isCoachFlow && proposal && (
          <ProspectsScreen
            flow={flow}
            proposal={proposal}
            manualProspects={manualProspects}
            selectedProspectIds={selectedProspectIds}
            onSelectedIdsChange={setSelectedProspectIds}
            onToggleProspect={toggleProspect}
            onAddManual={addManualProspect}
            principalName={principal?.name || ''}
            prospectDrafts={prospectDrafts}
            onProspectDraftChange={setProspectDraft}
            onProceedToLaunch={() => goToLaunched()}
          />
        )}

        {step === 'messages' && !isCoachFlow && (
          <MessagesScreen
            touches={proposal?.touches || []}
            flow={flow}
            answers={answers}
            channel={proposal?.campaign?.channel}
            campaignId={proposal?.campaign?.id}
            strategyNote={proposal?.strategyNote}
            onTouchUpdate={handleTouchUpdate}
            customOpener={proposal?.campaign?.customOpener !== false}
            customOpenerSaving={customOpenerSaving}
            onCustomOpenerChange={handleCustomOpenerChange}
            principalName={principal?.name || ''}
          />
        )}

        {step === 'launched' && (
          <LaunchedScreen
            campaign={proposal?.campaign}
            selectedProspects={buildSelectedProspects()}
            touches={proposal?.touches || []}
            launched={launched}
            launching={approving}
            importedCount={importedCount}
            onLaunch={handleApprove}
            launchLabel={editMode ? 'Save changes »' : 'Launch campaign »'}
            onBack={() => {
              // Only allowed pre-launch; post-launch, the AFTER panel replaces the back affordance.
              setLaunched(false);
              if (isCoachFlow) setStep('design');
              else goToProspects();
            }}
            onSeePipeline={() => navigate('/v2/board')}
            onAddMoreProspects={handleFullReset}
          />
        )}
      </WizardFrame>
      <LaunchConfirmModal
        state={launchModalState}
        count={Number(pendingCoachLaunch?.count || 0)}
        existingCount={Number(pendingCoachLaunch?.existingCount || 0)}
        confirming={approving && launchModalState === 'ready'}
        onClose={() => {
          setLaunchModalState('closed');
          setPendingCoachLaunch(null);
        }}
        onConfirm={handleCoachLaunchConfirm}
      />
    </>
  );
}
